import { useMemo, useState } from "react";
import { useResource } from "../../lib/hooks";
import { FilterBar, matches } from "../../components/FilterBar";

/**
 * The NDIS Support Catalogue, read-only. These are the official price CAPS
 * (National / Remote / Very-Remote) — the ceiling a support can be billed at,
 * not what TrustCare charges. A service links to one of these items and sets its
 * own charge (a % of the cap) in Settings → Services; this page is the reference
 * you pick from and sanity-check against.
 *
 * The catalogue is ~976 rows, so the list is filtered client-side and capped at
 * a readable number of rows — refine the search/filters to narrow it.
 */

interface NdisItem {
    id: string;
    item_number: string;
    name: string;
    reg_group: string | null;
    category: string | null;
    unit: string | null;
    quotable: boolean;
    price_national: string | null;
    price_remote: string | null;
    price_very_remote: string | null;
    price_year: string;
}

const ROW_CAP = 250;

const currency = (v: string | null): string =>
    v == null ? "—" : Number(v).toLocaleString("en-AU", { style: "currency", currency: "AUD" });

/** Unique, sorted, non-empty values of a column — for the filter dropdowns. */
const distinct = (rows: NdisItem[], pick: (r: NdisItem) => string | null) =>
    Array.from(new Set(rows.map(pick).filter((v): v is string => !!v)))
        .sort((a, b) => a.localeCompare(b))
        .map((v) => ({ value: v, label: v }));

export const NdisPricing = () => {
    // Pull the whole catalogue in one page (default limit is 500; ask for all).
    const items = useResource<NdisItem[]>("/ndis-items?limit=1000");

    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<Record<string, string>>({});

    const all = items.data ?? [];
    const categoryOptions = useMemo(() => distinct(all, (r) => r.category), [all]);
    const groupOptions = useMemo(() => distinct(all, (r) => r.reg_group), [all]);

    const filtered = all.filter((it) =>
        matches(search, it.item_number, it.name) &&
        (!filters.category || it.category === filters.category) &&
        (!filters.reg_group || it.reg_group === filters.reg_group) &&
        (!filters.quotable ||
            (filters.quotable === "quotable" ? it.quotable : !it.quotable))
    );
    const shown = filtered.slice(0, ROW_CAP);

    if (items.error) return <div className="error-banner">{items.error}</div>;

    return (
        <div>
            <FilterBar
                search={search} onSearch={setSearch}
                searchPlaceholder="Search by item number or name…"
                values={filters}
                onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value }))}
                filters={[
                    { name: "category", allLabel: "All categories", options: categoryOptions },
                    { name: "reg_group", allLabel: "All support groups", options: groupOptions },
                    { name: "quotable", allLabel: "Priced & quotable", options: [
                        { value: "priced", label: "Has a price cap" },
                        { value: "quotable", label: "Quotable (price on application)" },
                    ] },
                ]}
            />

            <div className="card">
                <div className="card-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <p className="muted" style={{ margin: 0 }}>
                        Official NDIS price caps{all.length ? ` · ${all[0]!.price_year}` : ""} — the ceiling, not your charge.
                        Set your charge per service in Settings → Services.
                    </p>
                    <span className="muted" style={{ fontSize: "0.8rem" }}>
                        {filtered.length === all.length ? `${all.length} items` : `${filtered.length} of ${all.length} items`}
                        {filtered.length > ROW_CAP ? ` · showing first ${ROW_CAP}` : ""}
                    </span>
                </div>
                <div className="card-body flush">
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>Item number</th>
                                    <th>Support</th>
                                    <th>Category</th>
                                    <th>Unit</th>
                                    <th style={{ textAlign: "right" }}>National</th>
                                    <th style={{ textAlign: "right" }}>Remote</th>
                                    <th style={{ textAlign: "right" }}>Very remote</th>
                                </tr>
                            </thead>
                            <tbody>
                                {shown.map((it) => (
                                    <tr key={it.id}>
                                        <td className="mono" style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{it.item_number}</td>
                                        <td>
                                            {it.name}
                                            {it.reg_group && <div style={{ fontSize: "0.72rem", color: "var(--muted)" }}>{it.reg_group}</div>}
                                        </td>
                                        <td>{it.category ?? "—"}</td>
                                        <td className="mono">{it.unit ?? "—"}</td>
                                        {it.quotable && it.price_national == null ? (
                                            <td colSpan={3} style={{ textAlign: "right" }}>
                                                <span className="chip info">Quotable — price on application</span>
                                            </td>
                                        ) : (
                                            <>
                                                <td className="mono" style={{ textAlign: "right" }}>{currency(it.price_national)}</td>
                                                <td className="mono" style={{ textAlign: "right" }}>{currency(it.price_remote)}</td>
                                                <td className="mono" style={{ textAlign: "right" }}>{currency(it.price_very_remote)}</td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                                {filtered.length === 0 && (
                                    <tr><td colSpan={7} className="empty">No catalogue items match.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};
